import { HttpClient } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { firstValueFrom } from "rxjs";

/**
 * ReservationsApiService (Angular 18).
 *
 * Replaces apps/b2b-ui/common/services/api/reservations/reservation.service.js,
 * which was a `.provider()` (not `.service()`) configured via
 * `ReservationApiServiceProvider.setEndpoint('/v2/api/reservations')` in the
 * legacy `api.service.js` config block. The endpoint is hardcoded here.
 *
 * Important contract difference vs the rest of `ApiService.*`: the legacy
 * service used `_promiseWrapper` to **unwrap** `response.data` and resolve
 * with the body directly. The single consumer
 * (`apps/b2b-ui/common/components/reservation-input/reservation-input.ts`)
 * relies on receiving `{ success, payload, error }` directly, not the
 * standard `{ data: { success, payload, error } }` envelope used by every
 * other migrated service. We preserve that behaviour by returning
 * `Promise<T>` instead of `Promise<AjsResponse<T>>` from this service.
 *
 * `update()` keeps the legacy 300ms debounce (`UtilsService.debounce`) and
 * also sends at most one PUT at a time for each reservation. Its live callers
 * (`CartEventsService.updateReservation()` and
 * `OrdersCheckoutController.updateReservation()`) call it on every quantity
 * change without waiting for the previous call. Parallel PUTs for the same
 * reservation make the backend insert duplicate reservation items and leak
 * inventory (DEV-15387). Every caller in a batch gets the response of the
 * PUT that sent the newest data. On an error only the newest caller is
 * rejected, so the callers' RESERVATION_DNE recovery creates one new
 * reservation, not one per call. `cancel()` drops the queued update for that
 * reservation, so no PUT (and no recovery POST) follows the DELETE. Dropped
 * calls never settle, as with the legacy debounce: the checkout page shows an
 * error notice for every rejection.
 */
const RESERVATIONS_BASE_V2 = "/v2/api/reservations";

interface ReservationItem {
  quantity: number;
  eventId?: string;
}

interface Reservation {
  id?: string;
  expirationTimestamp?: string;
  reservationItems?: ReservationItem[];
}

interface Waiter {
  resolve: (value: unknown) => void;
  reject: (reason: unknown) => void;
}

interface PendingUpdate {
  data: Reservation;
  waiters: Waiter[];
}

interface UpdateQueue {
  pending: PendingUpdate | null;
  timer: ReturnType<typeof setTimeout> | null;
  inFlight: boolean;
  cancelled: boolean;
}

export const RESERVATION_UPDATE_DEBOUNCE_MS = 300;

@Injectable({ providedIn: "root" })
export class ReservationsApiService {
  private readonly http = inject(HttpClient);
  private readonly updateQueues = new Map<string, UpdateQueue>();

  detail(id: string): Promise<unknown> {
    return firstValueFrom(
      this.http.get<unknown>(`${RESERVATIONS_BASE_V2}/${id}`),
    );
  }

  create(reservationItems: ReservationItem[]): Promise<unknown> {
    return firstValueFrom(
      this.http.post<unknown>(RESERVATIONS_BASE_V2, { reservationItems }),
    );
  }

  update(data: Reservation): Promise<unknown> {
    // Without an id there is nothing to merge on, so send it at once.
    if (data.id === undefined || data.id === null || data.id === "") {
      return firstValueFrom(this.http.put<unknown>(RESERVATIONS_BASE_V2, data));
    }
    const key = String(data.id);
    let queue = this.updateQueues.get(key);
    if (!queue) {
      queue = { pending: null, timer: null, inFlight: false, cancelled: false };
      this.updateQueues.set(key, queue);
    }
    const current = queue;

    return new Promise((resolve, reject) => {
      if (!current.pending) {
        current.pending = { data, waiters: [] };
      }
      current.pending.data = data;
      current.pending.waiters.push({ resolve, reject });

      if (current.timer !== null) {
        clearTimeout(current.timer);
      }
      current.timer = setTimeout(() => {
        current.timer = null;
        this.flushUpdate(key, current);
      }, RESERVATION_UPDATE_DEBOUNCE_MS);
    });
  }

  cancel(id: string): Promise<unknown> {
    const key = String(id);
    const queue = this.updateQueues.get(key);
    if (queue) {
      if (queue.timer !== null) {
        clearTimeout(queue.timer);
        queue.timer = null;
      }
      queue.pending = null;
      queue.cancelled = true;
      this.updateQueues.delete(key);
    }
    return firstValueFrom(
      this.http.delete<unknown>(`${RESERVATIONS_BASE_V2}/${id}`),
    );
  }

  private flushUpdate(key: string, queue: UpdateQueue): void {
    // The PUT in flight sends the next batch when it settles.
    if (queue.inFlight || !queue.pending) {
      return;
    }
    const batch = queue.pending;
    queue.pending = null;
    queue.inFlight = true;

    firstValueFrom(this.http.put<unknown>(RESERVATIONS_BASE_V2, batch.data))
      .then(
        (response) => {
          if (!queue.cancelled) {
            batch.waiters.forEach((w) => w.resolve(response));
          }
        },
        (error) => {
          if (!queue.cancelled) {
            batch.waiters[batch.waiters.length - 1].reject(error);
          }
        },
      )
      .finally(() => {
        queue.inFlight = false;
        if (queue.cancelled) {
          return;
        }
        if (queue.pending && queue.timer === null) {
          this.flushUpdate(key, queue);
        } else if (!queue.pending && queue.timer === null && this.updateQueues.get(key) === queue) {
          this.updateQueues.delete(key);
        }
      });
  }
}
